export const ROLE_CODE = {
  MEMBER: 'member',
  CLUB_DIRECTOR: 'club-director',
  CLUB_MANAGER: 'club-manager',
  CS_SALES_MANAGER: 'cs-sales-manager',
  CS_SALES_STAFF: 'cs-sales-staff',
  TRAINER_MANAGER: 'trainer-manager',
  TRAINER_STAFF: 'trainer-staff',
  AQUATIC_MANAGER: 'aquatic-manager',
  AQUATIC_STAFF: 'aquatic-staff',
} as const

export type RoleCode = (typeof ROLE_CODE)[keyof typeof ROLE_CODE]

export const CLUB_OWNER_ROLE_CODE = ROLE_CODE.CLUB_DIRECTOR

export const ROLE_RANK: Record<RoleCode, number> = {
  [ROLE_CODE.CLUB_DIRECTOR]: 100,
  [ROLE_CODE.CLUB_MANAGER]: 80,
  [ROLE_CODE.CS_SALES_MANAGER]: 60,
  [ROLE_CODE.TRAINER_MANAGER]: 60,
  [ROLE_CODE.AQUATIC_MANAGER]: 60,
  [ROLE_CODE.CS_SALES_STAFF]: 40,
  [ROLE_CODE.TRAINER_STAFF]: 40,
  [ROLE_CODE.AQUATIC_STAFF]: 40,
  [ROLE_CODE.MEMBER]: 0,
}

export type Department = 'MANAGEMENT' | 'SALES' | 'TRAINER' | 'AQUATIC'

export const ROLE_DEPARTMENT: Record<RoleCode, Department | null> = {
  [ROLE_CODE.CLUB_DIRECTOR]: 'MANAGEMENT',
  [ROLE_CODE.CLUB_MANAGER]: 'MANAGEMENT',
  [ROLE_CODE.CS_SALES_MANAGER]: 'SALES',
  [ROLE_CODE.CS_SALES_STAFF]: 'SALES',
  [ROLE_CODE.TRAINER_MANAGER]: 'TRAINER',
  [ROLE_CODE.TRAINER_STAFF]: 'TRAINER',
  [ROLE_CODE.AQUATIC_MANAGER]: 'AQUATIC',
  [ROLE_CODE.AQUATIC_STAFF]: 'AQUATIC',
  [ROLE_CODE.MEMBER]: null,
}

const DEPT_MANAGERS = new Set<RoleCode>([
  ROLE_CODE.CS_SALES_MANAGER,
  ROLE_CODE.TRAINER_MANAGER,
  ROLE_CODE.AQUATIC_MANAGER,
])

const STAFF_LEVEL = new Set<RoleCode>([
  ROLE_CODE.CS_SALES_STAFF,
  ROLE_CODE.TRAINER_STAFF,
  ROLE_CODE.AQUATIC_STAFF,
])

export const isClubOwner = (role: RoleCode) => role === CLUB_OWNER_ROLE_CODE
export const isClubManager = (role: RoleCode) => role === ROLE_CODE.CLUB_MANAGER
export const isDepartmentManager = (role: RoleCode) => DEPT_MANAGERS.has(role)
export const isStaffLevel = (role: RoleCode) => STAFF_LEVEL.has(role)

// Manager and above (club owner, club manager, dept managers, custom
// manager-position roles) — may work a lead/up-sell on behalf of its sales
// owner. Role position is the primary signal (covers custom roles); the
// chain-level codes are a fallback for legacy role rows whose position was
// never seeded. Mirrors the API's owner-check bypass.
export const isManagerLevelRole = (role?: { code?: string; position?: string } | null): boolean =>
  role?.position === 'MANAGER' ||
  role?.code === ROLE_CODE.CLUB_DIRECTOR ||
  role?.code === ROLE_CODE.CLUB_MANAGER

// QTY-2076: package pricing and terms are approval-gated. CS & Sale staff keep
// full read of the catalogue — they sell from it — but never create, edit,
// archive, un-archive or delete a package. Deliberately a role check rather
// than a permission code: the club owner, club manager and CS & Sale manager
// must be left exactly as they are, and the (code, action) rows this role is
// seeded with in the API are not something the CRM controls.
//
// This is UI-level only. The API is the enforcement point; the pages already
// turn a server 403 into the access-denied screen once it rejects them too.
export const canManagePackages = (role?: { code?: string } | null): boolean =>
  role?.code !== ROLE_CODE.CS_SALES_STAFF

// QTY-2069/2070/2072: team-wide sales data — the sales portfolio, the renewal
// forecast, and anyone else's Individual Sale Summary — is limited to the sales
// chain of command (club owner, club manager, CS & Sale manager). Role position
// is the primary signal so custom manager roles are covered; the explicit
// cs-sales-manager code is the fallback isManagerLevelRole's own comment warns
// about, for legacy role rows whose position was never seeded.
//
// The "sale" additional-permission preset deliberately does NOT grant this —
// the tickets scope it to ตำแหน่ง (position) only.
export const canViewSalesTeamWideData = (
  role?: { code?: string; position?: string } | null
): boolean => isManagerLevelRole(role) || role?.code === ROLE_CODE.CS_SALES_MANAGER

export const isSameDepartment = (a: RoleCode, b: RoleCode): boolean => {
  const da = ROLE_DEPARTMENT[a]
  const db = ROLE_DEPARTMENT[b]
  if (!da || !db) return false
  return da === db
}

export const isManagerOfDeptStaff = (actorRole: RoleCode, targetRole: RoleCode): boolean => {
  if (!isDepartmentManager(actorRole)) return false
  if (!isStaffLevel(targetRole)) return false
  return isSameDepartment(actorRole, targetRole)
}

// Dept-mgr acting on someone in their own department: either a dept-staff
// or a peer dept-manager. Covers edits that span the dept team (personal
// info, work schedule, training certs) but NOT role-position.
export const isManagerOfDeptTeam = (actorRole: RoleCode, targetRole: RoleCode): boolean => {
  if (!isDepartmentManager(actorRole)) return false
  if (!isSameDepartment(actorRole, targetRole)) return false
  return isStaffLevel(targetRole) || isDepartmentManager(targetRole)
}

export type StaffSection =
  | 'PERSONAL_INFO'
  | 'WORK_SCHEDULE'
  | 'ROLE_POSITION'
  | 'TRAINING_CERTS'
  | 'DELETE_ACCOUNT'
  // QTY-2041/2043: flipping a staff record between Active and In-Active
  // (soft-delete replacement). Owner + Club Manager only.
  | 'STAFF_STATUS'

export type StaffAction = 'READ' | 'CREATE' | 'UPDATE' | 'DELETE'

export type Scope = 'NONE' | 'SELF' | 'TEAM' | 'ALL_EXCEPT_OWNER' | 'ALL'

export type StaffPolicyActor = { userId: number; roleCode: RoleCode }
export type StaffPolicyTarget = { userId: number; roleCode: RoleCode }

export type StaffPolicyInput = {
  actor: StaffPolicyActor
  target: StaffPolicyTarget
  section: StaffSection
  action: StaffAction
}

export type StaffPolicyVerdict = {
  allow: boolean
  reason: string
  scopeUsed: Scope
}

const allow = (reason: string, scopeUsed: Scope): StaffPolicyVerdict => ({
  allow: true,
  reason,
  scopeUsed,
})

const deny = (reason: string, scopeUsed: Scope = 'NONE'): StaffPolicyVerdict => ({
  allow: false,
  reason,
  scopeUsed,
})

export const canActOnStaff = (input: StaffPolicyInput): StaffPolicyVerdict => {
  const { actor, target, section } = input
  const isSelf = actor.userId === target.userId

  switch (section) {
    case 'PERSONAL_INFO':
      return resolvePersonalInfo(input, isSelf)
    case 'WORK_SCHEDULE':
      return resolveWorkSchedule(input, isSelf)
    case 'ROLE_POSITION':
      return resolveRolePosition(input, isSelf)
    case 'TRAINING_CERTS':
      return resolveTrainingCerts(input, isSelf)
    case 'DELETE_ACCOUNT':
      return resolveDeleteAccount(input)
    case 'STAFF_STATUS':
      return resolveStaffStatus(input, isSelf)
  }
}

const resolvePersonalInfo = (input: StaffPolicyInput, isSelf: boolean): StaffPolicyVerdict => {
  const { actor, target, action } = input

  if (action === 'READ') {
    return allow('READ_ALL', isSelf ? 'SELF' : 'ALL')
  }

  if (action === 'UPDATE') {
    if (isSelf) return allow('SELF_EDIT', 'SELF')
    if (target.roleCode === ROLE_CODE.MEMBER) return allow('MEMBER_TARGET_OPEN', 'ALL')
    if (isClubOwner(target.roleCode) && !isClubOwner(actor.roleCode)) return deny('CO_PROTECTED')
    if (actor.roleCode === ROLE_CODE.CLUB_DIRECTOR) return allow('CO_FULL', 'ALL')
    if (actor.roleCode === ROLE_CODE.CLUB_MANAGER)
      return allow('CM_ALL_EXCEPT_CO', 'ALL_EXCEPT_OWNER')
    if (isManagerOfDeptTeam(actor.roleCode, target.roleCode))
      return allow('DEPT_MGR_DEPT_TEAM', 'TEAM')
    return deny('OUT_OF_SCOPE')
  }

  return deny('UNSUPPORTED_ACTION')
}

const resolveWorkSchedule = (input: StaffPolicyInput, isSelf: boolean): StaffPolicyVerdict => {
  const { actor, target, action } = input

  if (action === 'READ') {
    return allow('READ_ALL', isSelf ? 'SELF' : 'ALL')
  }

  if (action === 'UPDATE') {
    if (isSelf) return allow('SELF_EDIT', 'SELF')
    if (isClubOwner(target.roleCode) && !isClubOwner(actor.roleCode)) return deny('CO_PROTECTED')
    if (actor.roleCode === ROLE_CODE.CLUB_DIRECTOR) return allow('CO_FULL', 'ALL')
    if (actor.roleCode === ROLE_CODE.CLUB_MANAGER)
      return allow('CM_ALL_EXCEPT_CO', 'ALL_EXCEPT_OWNER')
    if (isManagerOfDeptTeam(actor.roleCode, target.roleCode))
      return allow('DEPT_MGR_DEPT_TEAM', 'TEAM')
    return deny('OUT_OF_SCOPE')
  }

  return deny('UNSUPPORTED_ACTION')
}

const resolveRolePosition = (input: StaffPolicyInput, isSelf: boolean): StaffPolicyVerdict => {
  const { actor, target, action } = input

  if (action === 'READ') {
    return allow('READ_ALL', isSelf ? 'SELF' : 'ALL')
  }

  if (action === 'UPDATE') {
    if (isClubOwner(target.roleCode) && !isClubOwner(actor.roleCode)) return deny('CO_PROTECTED')
    if (actor.roleCode === ROLE_CODE.CLUB_DIRECTOR) return allow('CO_FULL', 'ALL')
    if (actor.roleCode === ROLE_CODE.CLUB_MANAGER)
      return allow('CM_ALL_EXCEPT_CO', 'ALL_EXCEPT_OWNER')
    if (isManagerOfDeptStaff(actor.roleCode, target.roleCode))
      return allow('DEPT_MGR_DEPT_STAFF', 'TEAM')
    return deny('NO_ROLE_EDIT_FOR_ROLE')
  }

  return deny('UNSUPPORTED_ACTION')
}

const resolveTrainingCerts = (input: StaffPolicyInput, isSelf: boolean): StaffPolicyVerdict => {
  const { actor, target, action } = input

  if (action === 'READ') {
    return allow('READ_ALL', isSelf ? 'SELF' : 'ALL')
  }

  if (action === 'CREATE' || action === 'UPDATE' || action === 'DELETE') {
    if (
      actor.roleCode === ROLE_CODE.CS_SALES_MANAGER ||
      actor.roleCode === ROLE_CODE.CS_SALES_STAFF
    ) {
      return deny('CSS_NO_CERT_WRITE')
    }
    if (isClubOwner(target.roleCode) && !isClubOwner(actor.roleCode)) return deny('CO_PROTECTED')
    if (actor.roleCode === ROLE_CODE.CLUB_DIRECTOR) return allow('CO_FULL', 'ALL')
    if (actor.roleCode === ROLE_CODE.CLUB_MANAGER) return deny('CM_VIEW_ONLY_CERTS')
    if (isSelf) {
      if (
        actor.roleCode === ROLE_CODE.TRAINER_MANAGER ||
        actor.roleCode === ROLE_CODE.AQUATIC_MANAGER
      ) {
        return allow('SELF_EDIT', 'SELF')
      }
      return deny('STAFF_LEVEL_NO_CERT_EDIT')
    }
    if (isManagerOfDeptTeam(actor.roleCode, target.roleCode))
      return allow('DEPT_MGR_DEPT_TEAM', 'TEAM')
    return deny('OUT_OF_SCOPE')
  }

  return deny('UNSUPPORTED_ACTION')
}

const resolveDeleteAccount = (input: StaffPolicyInput): StaffPolicyVerdict => {
  const { actor, target, action } = input

  if (action !== 'DELETE') return deny('UNSUPPORTED_ACTION')

  if (actor.roleCode === ROLE_CODE.CLUB_DIRECTOR) {
    return allow('CO_DELETE_ANY', 'ALL')
  }

  if (actor.roleCode === ROLE_CODE.CLUB_MANAGER) {
    if (isClubOwner(target.roleCode)) return deny('CO_PROTECTED')
    if (target.roleCode === ROLE_CODE.CLUB_MANAGER) return deny('CM_CANNOT_DELETE_PEER_CM')
    return allow('CM_DELETE_BELOW', 'ALL_EXCEPT_OWNER')
  }

  return deny('NO_DELETE_PERMISSION')
}

/**
 * QTY-2041/2043: who may flip a staff record to In-Active and back.
 *
 * Mirrors DELETE_ACCOUNT's shape — Club Owner acts on anyone, Club Manager on
 * everyone below them — with one extra rule: nobody may deactivate themselves,
 * which would lock the actor out of the console mid-session. The "last Club
 * Owner of the tenant" guard is deliberately NOT here: this page only ever
 * holds one page of staff, so the count has to come from the server.
 */
const resolveStaffStatus = (input: StaffPolicyInput, isSelf: boolean): StaffPolicyVerdict => {
  const { actor, target, action } = input

  if (action !== 'UPDATE') return deny('UNSUPPORTED_ACTION')

  if (isSelf) return deny('CANNOT_CHANGE_OWN_STATUS', 'SELF')

  if (actor.roleCode === ROLE_CODE.CLUB_DIRECTOR) {
    return allow('CO_STATUS_ANY', 'ALL')
  }

  if (actor.roleCode === ROLE_CODE.CLUB_MANAGER) {
    if (isClubOwner(target.roleCode)) return deny('CO_PROTECTED')
    if (target.roleCode === ROLE_CODE.CLUB_MANAGER) return deny('CM_CANNOT_CHANGE_PEER_CM')
    return allow('CM_STATUS_BELOW', 'ALL_EXCEPT_OWNER')
  }

  return deny('NO_STATUS_PERMISSION')
}

// Two ids that cannot collide with a real user, so the probe below is never
// mistaken for the actor acting on themselves.
const PROBE_ACTOR_ID = -1
const PROBE_TARGET_ID = -2

/**
 * QTY-2043: could this actor change *anyone's* status?
 *
 * The restore-instead-of-duplicate prompt on the create form knows the
 * conflicting record's id but not its role — the `DUPLICATE_IDENTITY` payload
 * carries no role — so the full per-target verdict cannot be computed in the
 * client. This is the actor half of that check: it keeps the prompt away from
 * roles that could never reactivate anybody, and the server still applies the
 * target-specific half (owner-protected, peer Club Manager, self).
 *
 * Derived from `canActOnStaff` rather than listing roles again, so it cannot
 * drift from `resolveStaffStatus`. The probe target is a staff-level record —
 * the most permissive case; an actor denied on that one is denied on every
 * target.
 */
export const canEverChangeStaffStatus = (actorRoleCode: RoleCode): boolean =>
  canActOnStaff({
    actor: { userId: PROBE_ACTOR_ID, roleCode: actorRoleCode },
    target: { userId: PROBE_TARGET_ID, roleCode: ROLE_CODE.TRAINER_STAFF },
    section: 'STAFF_STATUS',
    action: 'UPDATE',
  }).allow

export type RoleAssignmentInput = {
  actor: StaffPolicyActor
  target: StaffPolicyTarget
  newRoleCode: RoleCode
}

export const canAssignRole = (input: RoleAssignmentInput): StaffPolicyVerdict => {
  const { actor, target, newRoleCode } = input

  const targetUpdate = canActOnStaff({
    actor,
    target,
    section: 'ROLE_POSITION',
    action: 'UPDATE',
  })
  if (!targetUpdate.allow) return targetUpdate

  if (newRoleCode === ROLE_CODE.CLUB_DIRECTOR && actor.roleCode !== ROLE_CODE.CLUB_DIRECTOR) {
    return deny('ONLY_CO_CAN_ASSIGN_CO')
  }

  if (ROLE_RANK[newRoleCode] > ROLE_RANK[actor.roleCode]) {
    return deny('RANK_GUARD_NEW_ROLE_TOO_HIGH')
  }

  return allow('RANK_GUARD_OK', targetUpdate.scopeUsed)
}

export const assignableRoleCodes = (actorRoleCode: RoleCode): RoleCode[] => {
  return (Object.values(ROLE_CODE) as RoleCode[]).filter((candidate) => {
    if (candidate === ROLE_CODE.MEMBER) return false
    if (candidate === ROLE_CODE.CLUB_DIRECTOR && actorRoleCode !== ROLE_CODE.CLUB_DIRECTOR) {
      return false
    }
    return ROLE_RANK[candidate] <= ROLE_RANK[actorRoleCode]
  })
}
